import { useEffect, useState } from 'react';

import { mutations, queries, queryKeys } from 'RestAPIs/rapini';

import { Dialog, showSnackbar } from 'components/shared';
import { useDebounceValue } from 'usehooks-ts';
import AssignEmploymentGrid from 'components/shared/AssignEmploymentGrid/AssignEmploymentGrid';

import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import dayjs from 'dayjs';
import DisabledTextField from 'components/shared/DisabledTextField';
import styled from 'styled-components';

import type {
  MatchCandidateResponse,
  PeriodAssignmentResponse
} from 'generated/index';

type Props = {
  open: boolean;
  assignment?: PeriodAssignmentResponse;
  title?: string;
  debounceInMS?: number;
  handleClose: () => void;
};

function AssignmentInfo({
  assignment
}: {
  assignment: PeriodAssignmentResponse;
}) {
  'use memo';

  const { t } = useTranslation('common');
  const {
    startDate,
    endDate,
    function: functionResponse,
    organizationalUnit,
    hoursPerWeek
  } = assignment;

  return (
    <Box>
      <Box>
        <Typography variant={'h4'}>{t('periodAssignment.title')}</Typography>
      </Box>
      <Box
        sx={{
          paddingTop: '10px',
          width: '768px',
          borderBottom: '1px solid grey'
        }}
        display="grid"
        gridTemplateColumns="repeat(auto-fit, minmax(25%,1fr))"
      >
        {startDate && (
          <DisabledTextField
            label={t('startDate')}
            value={dayjs(startDate).format('DD-MM-YYYY')}
            gridColumn="span 1"
          />
        )}

        {endDate && (
          <DisabledTextField
            label={t('endDate')}
            value={dayjs(endDate).format('DD-MM-YYYY')}
            gridColumn="span 2"
          />
        )}

        <Box />
        {functionResponse && (
          <DisabledTextField
            label={t('function')}
            value={functionResponse.description}
          />
        )}
        {organizationalUnit && (
          <DisabledTextField
            label={t('planUnit')}
            value={organizationalUnit.name}
          />
        )}

        {!!hoursPerWeek && (
          <DisabledTextField
            label={t('hoursPerWeek')}
            value={hoursPerWeek}
            fullWidth
          />
        )}
      </Box>
    </Box>
  );
}

const CustomDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    width: '80%', // here you set custom width
    maxWidth: 'none'
  }
});

export default function AssignmentMatchDialog({
  open,
  handleClose,
  assignment,
  title,
  debounceInMS = 500
}: Props) {
  'use memo';

  const { t } = useTranslation('common');

  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState<string>('');
  const [showInactiveEmployments, setShowInactiveEmployments] =
    useState<boolean>(false);
  const [showDeployableAsFlex, setShowDeployableAsFlex] =
    useState<boolean>(false);
  const [debouncedQuery] = useDebounceValue(searchValue, debounceInMS);
  const { data: candidates, isFetching: isFetchingCandidates } =
    queries.useFindMatchCandidatesForPeriodAssignment(
      assignment?.entityId ?? '',
      debouncedQuery,
      true,
      showDeployableAsFlex,
      !showInactiveEmployments,
      {
        enabled: open && !!assignment?.entityId,
        keepPreviousData: true
      }
    );

  const { mutate: connectPeriodAssignment, isLoading } =
    mutations.useConnectPeriodAssignment();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowDeployableAsFlex(event.target.checked);
  };

  async function onAssign(candidate: MatchCandidateResponse) {
    assignment &&
      connectPeriodAssignment(
        {
          employmentEntityId: candidate.employmentEntityId,
          periodAssignmentEntityId: assignment.entityId
        },
        {
          onSuccess: () => {
            handleClose();
            queryClient.invalidateQueries(queryKeys.getPeriodAssignments.name);
          },
          onError: () => {
            const message = t('somethingWentWrong');
            showSnackbar({
              severity: 'error',
              message: message
            });
          }
        }
      );
  }

  useEffect(() => {
    if (open) {
      setSearchValue('');
      setShowDeployableAsFlex(false);
      setShowInactiveEmployments(false);
    }
  }, [open]);

  return (
    <CustomDialog
      open={open}
      maxWidth="extralarge"
      handleClose={handleClose}
      title={title ? `Medewerker koppelen: ${title}` : 'Medewerker koppelen'}
    >
      {assignment && <AssignmentInfo assignment={assignment} />}
      <Box sx={{ paddingTop: '20px', paddingBottom: '-25px' }}>
        <FormControlLabel
          label={t(`showDeployableAsFlex`)}
          control={
            <Switch checked={showDeployableAsFlex} onChange={handleChange} />
          }
        />
      </Box>
      {assignment && (
        <AssignEmploymentGrid
          candidates={candidates}
          isLoading={isLoading || isFetchingCandidates}
          onAssign={onAssign}
          onSearch={setSearchValue}
          onShowInactiveEmployments={setShowInactiveEmployments}
          hideAppointments
          hideDuties
          hideQualifications
          hideEmployeeType={false}
          showInactiveEmployments={showInactiveEmployments}
        />
      )}
    </CustomDialog>
  );
}
